/**
 * The single-line text control for every app (BL-1713, ratifying the `Input`
 * row of the ADR 030 Canon: one control, mounted inside the already-shared
 * `FormField`).
 *
 * The merge collapses two from-scratch text fields that did the same job:
 * admin's `TextInput` (a bare input that FormField labels and that takes the
 * phone sheet's touch size) and bidder-web's `BidderInput` (a field kit whose
 * input sits in a row with a prefix / suffix and a tenant-brand focus ring).
 * The invalid state, the adornment row and the touch size used to be wired
 * twice; they now live here once.
 *
 * What it is NOT: the label, hint, required marker and inline error. Those are
 * `FormField`'s (admin) and the bidder skin's kit around this control. Owning
 * them here too would be a second FormField (BL-1713 AC 1, corrected).
 *
 * WHY a `dialect` object: the look is deliberately different per app (ADR 030
 * § 3b), so it is DATA each skin declares once, the same contract as
 * `PhoneInput` (BL-1241) and `Button` (BL-1711). A tenant's brand colour is only
 * known at runtime, so it arrives as `ringStyle`, PhoneInput's sanctioned
 * dynamic-value carve-out.
 */
'use client';

import clsx from 'clsx';
import { forwardRef, useContext } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode, Ref } from 'react';
import { FieldSizeContext } from './FormField';

/**
 * Per-app appearance binding: class names only, no structure. Every slot is
 * optional, so a third app starts from the bare accessible control.
 */
export interface InputDialect {
    /** Classes on the input in every state (layout, padding, radius, focus, disabled). */
    base?: string;
    /** The input at rest: the resting border (and fill). */
    resting?: string;
    /** The input while invalid. Replaces `resting`. */
    invalid?: string;
    /** Added inside a phone sheet (`FieldSizeContext` = `touch`). */
    touch?: string;
    /** BL-2548: replaces `resting` inside a phone sheet (`SHEET_FIELD_BORDER`). */
    touchResting?: string;
    /** Added everywhere else. */
    nonTouch?: string;
    /** BL-2472: replaces `touch` while invalid (a field in error keeps its red while focused). */
    invalidTouch?: string;
    /** BL-2472: replaces `nonTouch` while invalid. */
    invalidNonTouch?: string;
    /** Added to the input when a `prefix` is shown (the inset that clears it). */
    withPrefix?: string;
    /**
     * The row that holds prefix + input + suffix. A dialect that declares it
     * always renders the row (bidder's field DOM has one even with no
     * adornment); without it the row appears only for a prefix, suffix or
     * `rowRef`, and the bare input is admin's shape.
     */
    adornmentRow?: string;
    /** The span the prefix renders in. */
    prefixSlot?: string;
}

/** Props for {@link Input}. Native input props pass through. */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size'> {
    /** Per-app appearance binding. See {@link InputDialect}. */
    dialect: InputDialect;
    /** Error state: the invalid classes and `aria-invalid`. A caller's own `aria-invalid` wins. */
    invalid?: boolean;
    /** Leading adornment, rendered in the dialect's prefix slot. */
    prefix?: ReactNode;
    /** Trailing adornment, rendered after the input inside the row. */
    suffix?: ReactNode;
    /** Inline style for a runtime focus colour (a tenant brand). The caller's `style` wins per key. */
    ringStyle?: CSSProperties;
    /** Receives the adornment row (bidder's shake target). Giving it renders the row. */
    rowRef?: Ref<HTMLDivElement>;
}

/**
 * The shared text control. Bind a dialect in a per-app skin; call sites use the
 * skin. The forwarded ref reaches the `<input>`.
 *
 * @param props - {@link InputProps}
 * @returns The `<input>`, wrapped in the adornment row when the dialect declares
 *   one or a prefix, suffix or `rowRef` is given.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    { dialect, invalid = false, prefix, suffix, ringStyle, rowRef, className, style, ...rest },
    ref,
) {
    const isTouch = useContext(FieldSizeContext) === 'touch';
    const hasPrefix = prefix != null;
    const mergedStyle: CSSProperties | undefined =
        ringStyle || style ? { ...ringStyle, ...style } : undefined;

    const input = (
        <input
            ref={ref}
            className={clsx(
                dialect.base,
                invalid ? dialect.invalid : isTouch ? (dialect.touchResting ?? dialect.resting) : dialect.resting,
                isTouch
                    ? (invalid ? dialect.invalidTouch ?? dialect.touch : dialect.touch)
                    : (invalid ? dialect.invalidNonTouch ?? dialect.nonTouch : dialect.nonTouch),
                hasPrefix && dialect.withPrefix,
                className,
            )}
            style={mergedStyle}
            aria-invalid={invalid || undefined}
            {...rest}
        />
    );

    const needsRow = dialect.adornmentRow != null || hasPrefix || suffix != null || rowRef != null;
    if (!needsRow) return input;

    return (
        <div ref={rowRef} className={dialect.adornmentRow}>
            {hasPrefix ? <span className={dialect.prefixSlot}>{prefix}</span> : null}
            {input}
            {suffix}
        </div>
    );
});
